'use client'
import RatingsBarNew from '@/components/rating-bar-new'
import StarRating from '@/components/starraing-new'
import { Skeleton } from '@mantine/core'
import Image from 'next/image'
import RatingShowsSkeleton from './RatingShowsSkeleton'
import PropTypes from 'prop-types'
import { useTranslations } from 'next-intl'
import ReviewSection from './ReviewSection'

const reviewsBarData = {
  '5_rating': 0,
  '4_rating': 0,
  '3_rating': 0,
  '2_rating': 0,
  '1_rating': 0,
}

const ReviewsDesktop = ({
  reviewsDataNew,
  loadingReviews,
  totalCount,

  reviewCategories,
  platformData,
  skeletonData,
  id,
}) => {
  const t = useTranslations('propertydetails')
  const hasNoReviews = !loadingReviews && (reviewsDataNew?.data?.review?.length ?? 0) === 0

  const SkeletonRatingsBar = () => {
    return (
      <div className='w-[385px] space-y-[5.03px]'>
        {Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className='flex items-center gap-2'>
            {/* Star number skeleton */}
            <Skeleton height={15} width={12} className='rounded' />

            {/* Progress bar skeleton */}
            <div className='flex-1'>
              <Skeleton height={12} className='rounded-full' />
            </div>

            {/* Count skeleton */}
            <Skeleton height={12} width={12} className='rounded' />
          </div>
        ))}
      </div>
    )
  }
  return (
    <>
      <section className='pt-5 max-md:hidden mb-4'>
        {loadingReviews ? (
          <Skeleton visible={loadingReviews} height={10} width={100} />
        ) : (
          <p className='font-semibold text-[18px] leading-[150%] tracking-[0] text-[#1E1F24] mt-2 mb-6'>
            {hasNoReviews
              ? t('reviews.emptyState.sectionTitle')
              : t('reviews.sectionTitle')}
          </p>
        )}
        {hasNoReviews && (
          <div className='flex justify-start items-start w-full mt-6'>
            <div className='bg-white shadow-md rounded-2xl p-6 w-full max-w-sm flex flex-col items-start gap-3 border border-gray-100'>
              <span className='inline-flex items-center justify-center text-[#2CA81E] bg-[#ECFAF0] text-[11px] font-semibold rounded-[30px] px-2 py-0.5'>
                {t('badges.new')}
              </span>
              <h2 className='font-semibold text-[18px] leading-[150%] tracking-[0] text-[#1E1F24]'>
                {t('reviews.emptyState.title')}
              </h2>
              <p className='text-gray-500 text-sm text-start'>
                {t('reviews.emptyState.description')}
              </p>
            </div>
          </div>
        )}
        <div className='flex flex-row gap-10 items-center'>
          <div className='flex flex-col gap-3 mt-2'>
            {loadingReviews ? (
              <>
                <Skeleton height={87} width={99} />
                <Skeleton height={25} width={150} />
                <Skeleton height={26} width={129} />
              </>
            ) : (
              reviewsDataNew?.data?.review?.length !== 0 && (
                <>
                  <span className='font-semibold text-[62px] leading-[140%] tracking-[1%] align-middle ml-[6px] text-[#319341] opacity-100 md:w-full w-[99px] h-[87px]'>
                    {parseFloat(
                      reviewsDataNew?.data?.overall_rating || 0,
                    ).toFixed(1)}
                  </span>
                  <StarRating
                    rating={parseFloat(
                      reviewsDataNew?.data?.overall_rating || 0,
                    ).toFixed(1)}
                    className='h-fit items-center'
                    IconClassName='text-[#319341] text-sm align-middle'
                    size={25}
                  />
                  <span className='font-normal text-[16px] leading-[160%] tracking-[0] ml-[6px] text-[#1E1F24] opacity-100 w-[129px] h-[26px]'>
                    <>
                      {totalCount} {totalCount === 1 ? 'rating' : 'ratings'}
                    </>
                  </span>
                </>
              )
            )}
          </div>

          {loadingReviews ? (
            <SkeletonRatingsBar value={loadingReviews} />
          ) : (
            reviewsDataNew?.data?.review?.length !== 0 && (
              <RatingsBarNew
                reviews={reviewsDataNew?.data?.reviews ?? reviewsBarData}
              />
            )
          )}
        </div>

        {loadingReviews ? (
          <div className='w-full h-[93px] rotate-0 opacity-100 gap-[56px] text-white flex flex-row my-12'>
            {/* Value for Money Skeleton */}
            <div className='w-[110px] h-[93px] rotate-0 opacity-100 gap-3'>
              <div className='flex flex-col justify-start items-start'>
                <Skeleton height={21} width={21} />
                <div className='mt-4'>
                  <Skeleton height={16} width={85} className='mb-1' />
                </div>
                <Skeleton height={20} width={30} />
              </div>
            </div>

            {/* Check-in Experience Skeleton */}
            <div className='w-[150px] h-[93px] rotate-0 opacity-100 gap-3'>
              <div className='flex flex-col justify-start items-start'>
                <Skeleton height={21} width={21} />
                <div className='mt-4'>
                  <Skeleton height={16} width={120} className='mb-1' />
                </div>
                <Skeleton height={20} width={30} />
              </div>
            </div>

            {/* Safety & Security Skeleton */}
            <div className='w-[110px] h-[93px] rotate-0 opacity-100 gap-3'>
              <div className='flex flex-col justify-start items-start'>
                <Skeleton height={21} width={21} />
                <div className='mt-4'>
                  <Skeleton height={16} width={95} className='mb-1' />
                </div>
                <Skeleton height={20} width={30} />
              </div>
            </div>

            {/* Accuracy of Photos Skeleton */}
            <div className='w-[180px] h-[93px] rotate-0 opacity-100 gap-3'>
              <div className='flex flex-col justify-start items-start'>
                <Skeleton height={21} width={21} />
                <div className='mt-4'>
                  <Skeleton height={16} width={130} className='mb-1' />
                </div>
                <Skeleton height={20} width={30} />
              </div>
            </div>
          </div>
        ) : (
          reviewsDataNew?.data?.review?.length !== 0 && (
            <div
              className={`w-full h-[93px] flex-row gap-[56px] my-12 text-white ${Object.entries(reviewsDataNew?.data?.average_per_category ?? {}).length === 0 ? 'md:hidden' : 'md:flex'}`}
            >
              {reviewCategories.map(
                ({ key, label, icon, width }) =>
                  reviewsDataNew?.data?.average_per_category?.[key] && (
                    <div key={key} className={`${width} h-[93px] gap-3`}>
                      <div className='flex flex-col items-start'>
                        <Image src={icon} alt={label} width={21} height={21} />
                        <span className='font-normal text-[13px] leading-[187%] text-[#191919] mt-4'>
                          {label}
                        </span>
                        <p className='font-medium text-[17px] leading-[160%] text-[#08090E]'>
                          {reviewsDataNew?.data?.average_per_category?.[key] ??
                            0.0}
                        </p>
                      </div>
                    </div>
                  ),
              )}
            </div>
          )
        )}

        {loadingReviews ? (
          <div className='w-full h-[118.375px] mt-5 flex gap-4'>
            {skeletonData.map((item) => (
              <div
                key={item.id}
                className={`w-[148px] h-[115px] pt-4 pr-[18px] pb-4 pl-[18px] rounded-[10px] bg-[#F5F5F5]
           ${item.hasBorder ? 'border border-[#949494]' : ''}
           ${item.hasSpecialLayout ? 'flex justify-center items-center' : ''}`}
              >
                <div className='flex flex-col items-center'>
                  <Skeleton height={item.iconSize} width={item.iconSize} />
                  <div className='mt-2'>
                    <Skeleton
                      height={16}
                      width={item.labelWidth}
                      className='mb-1'
                    />
                  </div>
                  <Skeleton height={18} width={25} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          reviewsDataNew?.data?.review?.length !== 0 && (
            <div className='w-full h-[118.375px] mt-5 flex gap-4'>
              {platformData.map((platform) => {
                const ratingValue = parseFloat(platform.rating) || 0

                // Don't render if rating is 0
                if (ratingValue === 0) {
                  return null
                }

                return (
                  <div
                    key={platform.id}
                    className={`w-[148px] h-[115px] pt-4 pr-[18px] pb-4 pl-[18px] rounded-[10px] bg-[#F5F5F5]
             ${platform.hasBorder ? 'border border-[#949494]' : ''}
             ${platform.hasSpecialLayout ? 'flex justify-center items-center' : ''}`}
                  >
                    <div className='flex flex-col items-center'>
                      <Image
                        src={platform.icon}
                        alt={platform.alt}
                        width={platform.id === 'overall' ? 31 : 30}
                        height={platform.id === 'overall' ? 31 : 30}
                      />
                      <span className='font-normal text-[13px] leading-[187%] tracking-normal text-[#191919] mt-2'>
                        {platform.label}
                      </span>
                      <p className='text-[#191919] font-semibold text-[14px] leading-[160%] tracking-normal'>
                        {ratingValue.toFixed(1)}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )
        )}
      </section>

      <section className='max-md:hidden'>
        {loadingReviews ? (
          <div className='flex flex-wrap gap-4 mt-10 mb-10'>
            <div className='flex flex-wrap gap-8 my-2 w-full'>
              {/* Create skeleton placeholders that match ReviewCard layout */}
              {Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className='w-full md:w-[48%]'>
                  <div className='bg-white rounded-lg p-4 shadow-lg w-full min-w-[250px] max-w-full'>
                    {/* Top Row: Avatar + Name & Rating skeleton */}
                    <div className='flex gap-4 mb-3'>
                      {/* Avatar skeleton */}
                      <Skeleton
                        height={48}
                        width={48}
                        circle
                        className='flex-shrink-0'
                      />

                      {/* Name + Rating section skeleton */}
                      <div className='flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between'>
                        <div className='flex-1'>
                          {/* Name skeleton */}
                          <Skeleton height={16} width='45%' className='mb-1' />
                          {/* Date skeleton */}
                          <Skeleton height={14} width='35%' />
                        </div>

                        {/* Rating skeleton */}
                        <div className='flex items-center gap-1 mt-1 sm:mt-0'>
                          {/* Star rating skeleton */}
                          <Skeleton height={20} width={80} />
                          {/* Rating number skeleton */}
                          <Skeleton height={16} width={25} />
                        </div>
                      </div>
                    </div>

                    {/* Review Text skeleton - Below the avatar row */}
                    <div className='ml-0 space-y-2'>
                      <Skeleton height={14} width='100%' />
                      <Skeleton height={14} width='95%' />
                      <Skeleton height={14} width='75%' />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Render the ReviewSection component
          reviewsDataNew?.data?.review?.length !== 0 && (
            <ReviewSection
              id={id}
              initialReviews={reviewsDataNew?.data?.review ?? []}
              initialHasMore={Boolean(reviewsDataNew?.data?.next)}
              initialTotalCount={Number(reviewsDataNew?.data?.totalCount) || 0}
            />
          )
        )}
        {reviewsDataNew?.data?.review?.length !== 0 && (
          <hr className='text-[#00000033] mb-9 md:hidden' />
        )}
      </section>
    </>
  )
}

ReviewsDesktop.propTypes = {
  reviewsDataNew: PropTypes.shape({
    data: PropTypes.shape({
      review: PropTypes.arrayOf(PropTypes.object),

      overall_rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

      reviews: PropTypes.shape({
        '5_rating': PropTypes.number,
        '4_rating': PropTypes.number,
        '3_rating': PropTypes.number,
        '2_rating': PropTypes.number,
        '1_rating': PropTypes.number,
      }),

      average_per_category: PropTypes.object,
    }),
  }),

  loadingReviews: PropTypes.bool.isRequired,
  totalCount: PropTypes.number.isRequired,

  reviewCategories: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.any.isRequired,
      width: PropTypes.string,
    }),
  ).isRequired,

  platformData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      icon: PropTypes.any,
      alt: PropTypes.string,
      hasBorder: PropTypes.bool,
      hasSpecialLayout: PropTypes.bool,
    }),
  ).isRequired,

  skeletonData: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      iconSize: PropTypes.number,
      labelWidth: PropTypes.number,
      hasBorder: PropTypes.bool,
      hasSpecialLayout: PropTypes.bool,
    }),
  ).isRequired,

  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
}

export default ReviewsDesktop
